import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '@completion/services';
import { Observable } from 'rxjs';
import { withLatestFrom, filter } from 'rxjs/operators';

import {
  DocumentActionTypes,
  DocumentLoad,
  DocumentUpdate,
  DocumentCreate,
  DocumentUpdateSuccess,
  DocumentCreateSuccess,
  SendApprovalReminder,
  UpdateNode,
  LoadProcedureChecks,
  UpdateNodeSuccess,
  LoadProcedureChecksSuccess,
  UploadDocumentPdf,
  UploadDocumentPdfSuccess,
  UploadDocumentPdfFailure,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentCpId, getCurrentDocument, getCurrentProject } from '@completion/selectors';
import { DocumentService } from '../../services/document.service';
import { ProjectAccessService } from '../../services/project-access.service';
import { DocumentLoadSuccess } from '@completion/actions';
import { DocumentNode, ProcedureDocument, Project } from '@completion/models';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class DocumentEffect {
  constructor(
    private actions$: Actions,
    private service: DocumentService,
    private readonly projectAccess: ProjectAccessService,
    private store: Store<State>,
    private toastr: NotificationService,
  ) {}

  loadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DocumentLoad>(DocumentActionTypes.DocumentLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      // prevent calling service with null cpId
      filter(([_, currentProject, currentCpId]) => !!currentProject && currentCpId != null),

      trackResource<ProcedureDocument>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'loadDocument',
        serviceArgs: ([_, currentProject, currentCpId]) => [currentProject.id, currentCpId],
        onSuccess: (doc) => (doc ? new DocumentLoadSuccess(doc) : { type: '[Document] Load Empty' }),
      }),
    ),
  );

  updateDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DocumentUpdate>(DocumentActionTypes.DocumentUpdate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, currentCpId]) => !!currentProject && currentCpId != null),
      trackResource<ProcedureDocument, DocumentUpdate>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'updateDocument',
        serviceArgs: ([action, currentProject, currentCpId]) => [currentProject.id, currentCpId, (action as DocumentUpdate).document],
        onSuccess: (doc) => new DocumentUpdateSuccess(doc as ProcedureDocument),
      }),
    ),
  );

  createDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DocumentCreate>(DocumentActionTypes.DocumentCreate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, currentCpId]) => !!currentProject && currentCpId != null),
      trackResource<ProcedureDocument, DocumentCreate>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'createDocument',
        serviceArgs: ([action, currentProject, currentCpId]) => [currentProject.id, currentCpId, (action as DocumentCreate).document],
        onSuccess: (doc) => new DocumentCreateSuccess(doc as ProcedureDocument),
      }),
    ),
  );

  sendReminder$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DocumentUpdateSuccess>(DocumentActionTypes.SendApprovalReminder),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentDocument) as Observable<ProcedureDocument>,
      ),
      filter(([_, currentProject, currentCpId, _currentDocument]) => !!currentProject && currentCpId != null && !!getCurrentDocument),
      trackResource<ProcedureDocument, SendApprovalReminder>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'sendApprovalReminder',
        serviceArgs: ([action, currentProject, currentCpId, currentDocument]) => [
          currentProject.id,
          currentCpId,
          currentDocument.id,
          action.userId,
        ],
        onSuccess: void 0,
      }),
    ),
  );

  updateNode$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateNode>(DocumentActionTypes.UpdateNode),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, currentCpId]) => !!currentProject && currentCpId != null),
      trackResource<DocumentNode, UpdateNode>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'updateNode',
        serviceArgs: ([action, currentProject, currentCpId]) => [currentProject.id, currentCpId, action.node.id, action.node],
        onSuccess: (node) => new UpdateNodeSuccess(node),
      }),
    ),
  );

  loadCheckTestNodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadProcedureChecks>(DocumentActionTypes.LoadProcedureChecks),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.projectAccess.isOperatorCP$,
      ),
      filter(([_, currentProject, currentCpId, canOperateCP]) => !!currentProject && currentCpId != null && canOperateCP),
      trackResource<DocumentNode[], LoadProcedureChecks>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'loadCheckTestNodes',
        serviceArgs: ([_, currentProject, currentCpId]) => [currentProject.id, currentCpId],
        onSuccess: (nodes) => new LoadProcedureChecksSuccess(nodes),
      }),
    ),
  );

  uploadDocumentPdf$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UploadDocumentPdf>(DocumentActionTypes.UploadDocumentPdf),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentDocument) as Observable<ProcedureDocument>,
      ),
      filter(
        ([_, currentProject, currentCpId, currentDocument]) =>
          !!currentProject && currentCpId != null && !!currentDocument && !!currentDocument.id,
      ),
      trackResource<void, UploadDocumentPdf>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'uploadDocumentPdf',
        serviceArgs: ([action, currentProject, currentCpId, currentDocument]) => [
          currentProject.id,
          currentCpId,
          currentDocument.id,
          action.pdfBlob,
        ],
        onSuccess: () => new UploadDocumentPdfSuccess(),
        onError: (error) => new UploadDocumentPdfFailure(error),
      }),
    ),
  );
}
